import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { styled } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  MenuItem,
  Switch,
  TextField,
  TextFieldProps,
  Paper
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import Title from '../../components/Title';
import {
  createTimingTariffAsync,
  deleteTariffAsync,
  getTariffsAsync,
  selectBalance,
  updateTariffAsync
} from '../../redux/slices/balanceSlice';
import BonusTopUpBlock from './BonusTopUpBlock';
import { InputChangeEvent } from '../../resources/utils';
import {
  getContractsStatusesAsync,
  selectContracts
} from '../../redux/slices/contractsSlice';
import { Tariff } from '../../resources/types/balanceTypes';
import TariffsTable from './TariffsTable';
import UserTypeSelect from '../../containers/UserTypeSelect';
import InputMask from 'react-input-mask';
import { formatNumberToDate } from '../../resources/functions';

const PREFIX = 'TariffsPage';

const classes = {
  header: `${PREFIX}-header`,
  addButton: `${PREFIX}-addButton`,
  tableContainer: `${PREFIX}-tableContainer`
};

const Root = styled('div')({
  [`& .${classes.header}`]: {
    display: 'flex',
    alignItems: 'center'
  },
  [`& .${classes.addButton}`]: {
    marginLeft: 30
  },
  [`& .${classes.tableContainer}`]: {
    marginTop: 40
  }
});

const newTariff: Tariff = {
  userType: 'normal',
  price: '',
  type: '',
  name: '',
  maxTime: '',
  from: '',
  to: '',
  timingPrice: null
};

interface TimingPriceEntry {
  seconds: string;
  pricePerMinute: string;
}

const TariffsPage = () => {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getTariffsAsync());
    dispatch(getContractsStatusesAsync());
  }, [dispatch]);

  const { tariffs } = useSelector(selectBalance);
  const contracts = useSelector(selectContracts);

  const [open, setOpen] = useState(false);
  const [selectedTariff, setSelectedTariff] = useState<Tariff | null>(null);
  const [isCurrentTarrifOpened, setIsCurrentTarrifOpened] =
    useState<boolean>(false);
  const [filteredTariffsTimingInterval, setFilteredTariffsTimingInterval] =
    useState<Tariff[] | []>([]);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<Tariff | null>();
  const [tariffTime, setTariffTime] = useState<{
    from: string | number | undefined;
    to: string | number | undefined;
  }>({ from: '', to: '' });
  const [isShowedTimeFields, setIsShowedTimeFields] = useState<boolean>(false);
  const [timingPriceEnabled, setTimingPriceEnabled] = useState<boolean>(false);
  const [timingPriceEntries, setTimingPriceEntries] = useState<TimingPriceEntry[]>([
    { seconds: '0', pricePerMinute: '' }
  ]);

  useEffect(() => {
    selectedTimeSlot &&
      setTariffTime({ from: selectedTimeSlot?.from, to: selectedTimeSlot?.to });
  }, [selectedTimeSlot]);

  useEffect(() => {
    if (selectedTariff?.timingPrice && Object.keys(selectedTariff.timingPrice).length > 0) {
      setTimingPriceEnabled(true);
      const entries = Object.entries(selectedTariff.timingPrice).map(([seconds, price]) => ({
        seconds: String(seconds),
        pricePerMinute: String(price)
      }));
      setTimingPriceEntries(entries.length > 0 ? entries : [{ seconds: '0', pricePerMinute: '' }]);
    } else {
      setTimingPriceEnabled(false);
      setTimingPriceEntries([{ seconds: '0', pricePerMinute: '' }]);
    }
  }, [selectedTariff?._id, selectedTariff?.timingPrice]);

  const toggleDialog = useCallback(
    (tariff: Tariff | null) => () => {
      setOpen(prevState => !prevState);
      if (tariff !== null) {
        setSelectedTariff(tariff);
        setIsCurrentTarrifOpened(true);
      } else {
        setSelectedTariff(newTariff);
        setTimingPriceEnabled(false);
        setTimingPriceEntries([{ seconds: '0', pricePerMinute: '' }]);
      }
    },
    []
  );

  const handleChangeTariff =
    (prop: keyof Tariff, price?: boolean) => (e: InputChangeEvent) => {
      if (prop === 'from' || 'to') {
        setTariffTime({ ...tariffTime, [prop]: e.target.value });
      }
      if (selectedTariff) {
        setSelectedTariff({
          ...selectedTariff,
          [prop]: e.target.value,
          price: price ? e.target.value : selectedTariff.price
        });
      }
    };

  const updateTariff = () => {
    if (selectedTariff) {
      const tarrifStartFrom =
        selectedTariff?.from && String(selectedTariff?.from).replace(':', '');
      const tarrifFinishTo =
        selectedTariff?.to && String(selectedTariff?.to).replace(':', '');
      
      let timingPriceData: { [key: string]: number } | null = null;
      if (selectedTariff.type === 'normal' && timingPriceEnabled && timingPriceEntries.length > 0) {
        timingPriceData = {};
        timingPriceEntries.forEach(entry => {
          if (entry.seconds !== '' && entry.pricePerMinute !== '') {
            timingPriceData![entry.seconds] = Number(entry.pricePerMinute);
          }
        });
        if (Object.keys(timingPriceData).length === 0) {
          timingPriceData = null;
        }
      }

      const reqBody = {
        ...selectedTariff,
        from: tarrifStartFrom,
        to: tarrifFinishTo,
        timingPrice: timingPriceData
      };
      if (tarrifStartFrom && tarrifFinishTo && !selectedTimeSlot) {
        dispatch(
          createTimingTariffAsync(selectedTariff.type, reqBody, () => {
            setOpen(false);
            setIsCurrentTarrifOpened(false);
          })
        );
      } else {
        dispatch(
          updateTariffAsync(selectedTariff.type, reqBody, () => {
            setOpen(false);
            setIsCurrentTarrifOpened(false);
            setTariffTime({ from: '', to: '' });
          })
        );
      }
    }
  };

  const deleteTariff = (_id: string | undefined) => {
    dispatch(deleteTariffAsync(_id!));
  };

  const normalTariffs = useMemo(
    () => tariffs.items.filter(item => item.userType === 'normal'),
    [tariffs.items]
  );

  const vipTariffs = useMemo(
    () => tariffs.items.filter(item => item.userType === 'vip'),
    [tariffs.items]
  );

  useEffect(() => {
    setFilteredTariffsTimingInterval(
      selectedTariff?.userType === 'normal'
        ? normalTariffs.filter(el => {
            return (
              selectedTariff?.name === el.name &&
              selectedTariff?.type === el.type &&
              el.from &&
              el.to
            );
          })
        : vipTariffs.filter(el => {
            return (
              selectedTariff?.name === el.name &&
              selectedTariff?.type === el.type &&
              el.from &&
              el.to
            );
          })
    );
  }, [selectedTariff, normalTariffs, vipTariffs]);

  const addTimeFields = () => {
    setIsShowedTimeFields(true);
    setTariffTime({ from: '', to: '' });
    setSelectedTimeSlot(null);
  };

  const handleClickOnTimeInterval = (_id: string | undefined) => {
    setSelectedTimeSlot(
      filteredTariffsTimingInterval.find(el => el._id === _id)
    );
  };

  return (
    <Root>
      <div className={classes.header}>
        <Title>Тарифи</Title>

        <Button
          className={classes.addButton}
          color='primary'
          startIcon={<AddIcon />}
          variant='contained'
          onClick={toggleDialog(null)}
        >
          Додати
        </Button>
      </div>

      {Boolean(normalTariffs.length) && (
        <Paper sx={{ border: (theme) => theme.palette.mode === 'light' ? '2px solid #FFFFFF' : 'none', borderRadius: 2, padding: '20px', marginTop: 3 }}>
          <Title variant='h6'>Налаштування для звичайного клієнта</Title>
          <TariffsTable
            items={normalTariffs}
            openDialog={toggleDialog}
            onDelete={deleteTariff}
          />
        </Paper>
      )}

      {Boolean(vipTariffs.length) && (
        <Paper sx={{ border: (theme) => theme.palette.mode === 'light' ? '2px solid #FFFFFF' : 'none', borderRadius: 2, padding: '20px', marginTop: 3 }}>
          <Title variant='h6'>Налаштування для VIP клієнта</Title>
          <TariffsTable
            items={vipTariffs}
            openDialog={toggleDialog}
            onDelete={deleteTariff}
          />
        </Paper>
      )}

      <BonusTopUpBlock />

      <Dialog
        fullWidth
        maxWidth='sm'
        open={open}
        onClose={() => {
          setOpen(false);
          setTariffTime({ from: '', to: '' });
          setIsCurrentTarrifOpened(false);
          setSelectedTimeSlot(null);
          setIsShowedTimeFields(false);
        }}
      >
        <DialogTitle>
          Налаштування тарифу
        </DialogTitle>
        <DialogContent>
          <Box mb={2}>
            {filteredTariffsTimingInterval.map(el => (
              <Chip
                key={el._id}
                label={`${formatNumberToDate(el.from)}-${formatNumberToDate(
                  el.to
                )}`}
                variant='outlined'
                sx={{
                  borderColor: '#4ECDC4',
                  cursor: 'pointer',
                  marginRight: 1,
                  marginBottom: 1
                }}
                onClick={() => handleClickOnTimeInterval(el._id)}
                onDelete={() => deleteTariff(el._id)}
              />
            ))}
          </Box>
          <TextField
            fullWidth
            label='Назва'
            value={selectedTariff?.name}
            onChange={handleChangeTariff('name')}
            disabled={!!selectedTimeSlot}
          />
          <TextField
            fullWidth
            label='Ціна'
            margin='normal'
            type='number'
            disabled={!!selectedTimeSlot}
            value={
              selectedTimeSlot
                ? selectedTimeSlot.price
                : selectedTariff?.price
            }
            onChange={handleChangeTariff('price', true)}
          />
          {selectedTariff?.type === 'start' && (
            <TextField
              fullWidth
              label='Сума заморозки (hold)'
              margin='normal'
              type='number'
              value={selectedTariff?.holdAmount ?? ''}
              onChange={handleChangeTariff('holdAmount')}
            />
          )}
          {selectedTariff?.type === 'start' && (
            <TextField
              fullWidth
              label='Вартість паркування в іншому готелі (relocationFee)'
              margin='normal'
              type='number'
              value={selectedTariff?.relocationFee ?? ''}
              onChange={handleChangeTariff('relocationFee')}
            />
          )}
          <UserTypeSelect
            value={selectedTariff?.userType}
            onChange={handleChangeTariff('userType')}
          />
          <TextField
            fullWidth
            select
            disabled={Boolean(selectedTariff?._id)}
            label='Тип'
            margin='normal'
            value={selectedTariff?.type}
            onChange={handleChangeTariff('type')}
          >
            {contracts.statuses.items.map(status => (
              <MenuItem key={status} value={status}>
                {status}
              </MenuItem>
            ))}
          </TextField>

          {selectedTariff?.type === 'normal' && (
            <Paper sx={{ p: 2, mt: 2, mb: 2, bgcolor: 'rgba(78, 205, 196, 0.05)', border: '1px solid rgba(78, 205, 196, 0.2)', borderRadius: 2 }}>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Switch
                      checked={timingPriceEnabled}
                      onChange={(e) => setTimingPriceEnabled(e.target.checked)}
                      sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#4ECDC4' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#4ECDC4' } }}
                    />
                  }
                  label='Посекундна тарифікація'
                />
              </FormGroup>
              {timingPriceEnabled && (
                <Box sx={{ mt: 2 }}>
                  <Box sx={{ mb: 1, fontSize: 12, color: 'text.secondary' }}>
                    Налаштуйте ціну за хвилину для кожного часового інтервалу (в секундах)
                  </Box>
                  {timingPriceEntries.map((entry, index) => (
                    <Box key={index} sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 1 }}>
                      <TextField
                        size="small"
                        label="Від (сек)"
                        type="number"
                        value={entry.seconds}
                        onChange={(e) => {
                          const newEntries = [...timingPriceEntries];
                          newEntries[index].seconds = e.target.value;
                          setTimingPriceEntries(newEntries);
                        }}
                        sx={{ width: 120 }}
                      />
                      <TextField
                        size="small"
                        label="Ціна/хв"
                        type="number"
                        value={entry.pricePerMinute}
                        onChange={(e) => {
                          const newEntries = [...timingPriceEntries];
                          newEntries[index].pricePerMinute = e.target.value;
                          setTimingPriceEntries(newEntries);
                        }}
                        sx={{ width: 120 }}
                      />
                      {index > 0 && (
                        <Button
                          size="small"
                          color="error"
                          onClick={() => {
                            const newEntries = timingPriceEntries.filter((_, i) => i !== index);
                            setTimingPriceEntries(newEntries);
                          }}
                        >
                          Видалити
                        </Button>
                      )}
                    </Box>
                  ))}
                  <Button
                    size="small"
                    sx={{ mt: 1, color: '#4ECDC4' }}
                    onClick={() => setTimingPriceEntries([...timingPriceEntries, { seconds: '', pricePerMinute: '' }])}
                  >
                    + Додати інтервал
                  </Button>
                </Box>
              )}
            </Paper>
          )}

          {((isCurrentTarrifOpened && selectedTimeSlot) ||
            isShowedTimeFields) && (
            <Box>
              <InputMask
                mask='99:99'
                value={formatNumberToDate(tariffTime.from!)}
                onChange={handleChangeTariff('from')}
              >
                {(inputProps: JSX.IntrinsicAttributes & TextFieldProps) => (
                  <TextField
                    {...inputProps}
                    label='З'
                    margin='normal'
                    sx={{ marginRight: 5 }}
                  ></TextField>
                )}
              </InputMask>
              <InputMask
                mask='99:99'
                value={formatNumberToDate(tariffTime.to!)}
                onChange={handleChangeTariff('to')}
              >
                {(inputProps: JSX.IntrinsicAttributes & TextFieldProps) => {
                  return (
                    <TextField
                      {...inputProps}
                      label='До'
                      margin='normal'
                    ></TextField>
                  );
                }}
              </InputMask>
            </Box>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            justifyContent: isShowedTimeFields ? 'flex-end' : 'space-between'
          }}
        >
          {!isShowedTimeFields && (
            <Button color='primary' onClick={addTimeFields}>
              Додати підтариф з часовими проміжками
            </Button>
          )}
          <Button color='primary' onClick={updateTariff}>
            Зберегти
          </Button>
        </DialogActions>
      </Dialog>
    </Root>
  );
};

export default TariffsPage;
